import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { Lesson } from '@/types/lesson'
import { lessonService as defaultLessonService } from '@/services/lessonService'
import ContentReviewModal from '@/components/admin/ContentReviewModal'

export interface AdminContentReviewInboxPageProps {
  service?: Pick<
    typeof defaultLessonService,
    'getPendingReview' | 'approveLesson' | 'rejectLesson'
  >
}

/**
 * Admin inbox for lessons in `pending_review`. Clicking a row opens the
 * ContentReviewModal where admin can Approve or Reject with a note.
 */
export const AdminContentReviewInboxPage: React.FC<AdminContentReviewInboxPageProps> = ({
  service = defaultLessonService,
}) => {
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<Lesson | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await service.getPendingReview()
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to load lessons')
      }
      setLessons(res.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lessons')
    } finally {
      setLoading(false)
    }
  }, [service])

  useEffect(() => {
    reload()
  }, [reload])

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return lessons
    return lessons.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        (l.description || '').toLowerCase().includes(q),
    )
  }, [lessons, filter])

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Content Review Inbox</h1>
        <p className="text-gray-500 dark:text-text-muted text-sm">
          Lessons submitted by instructors awaiting your approval.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <input
          type="search"
          placeholder="Filter by title or description…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2 text-sm text-gray-900 dark:text-white"
          data-testid="inbox-filter"
        />
        <button
          type="button"
          onClick={reload}
          className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white text-sm hover:bg-gray-200 dark:hover:bg-white/10"
          data-testid="inbox-refresh"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <p className="text-gray-500 dark:text-text-muted" data-testid="inbox-loading">
          Loading…
        </p>
      )}
      {error && (
        <div role="alert" className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="p-8 text-center rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 border-dashed text-gray-500 dark:text-text-muted">
          No lessons awaiting review.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="inbox-table">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-text-muted uppercase tracking-wider border-b border-gray-200 dark:border-white/10">
              <th className="py-2">Title</th>
              <th className="py-2">Type</th>
              <th className="py-2 hidden md:table-cell">Submitted</th>
              <th className="py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((lesson) => (
              <tr
                key={lesson.id}
                className="border-b border-gray-200 dark:border-white/5 hover:bg-white/[0.02]"
                data-testid={`inbox-row-${lesson.id}`}
              >
                <td className="py-3 text-sm text-gray-900 dark:text-white">{lesson.title}</td>
                <td className="py-3 text-sm text-gray-500 dark:text-text-muted">{lesson.contentType}</td>
                <td className="py-3 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">
                  {lesson.submittedAt ? new Date(lesson.submittedAt).toLocaleString() : '-'}
                </td>
                <td className="py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setSelected(lesson)}
                    className="px-3 py-1.5 rounded-lg bg-primary/20 text-primary text-xs font-bold hover:bg-primary/30"
                    data-testid={`inbox-review-${lesson.id}`}
                  >
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {selected && (
        <ContentReviewModal
          lesson={selected}
          service={service}
          onClose={() => setSelected(null)}
          onActionComplete={reload}
        />
      )}
    </div>
  )
}

export default AdminContentReviewInboxPage
